import { Component, Input, OnDestroy, OnInit, TemplateRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

import { NgxUiLoaderService } from 'ngx-ui-loader';
import { Subscription, throwError } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { ConfirmComponent } from '../../../shared/components';

import { environment } from '../../../../environments/environment';
import { EventUpdatesService } from '../../../core/services/event-updates.service';
import { ToastService } from '../../../core/services/toast.service';
import { Store } from '@ngxs/store';
import { UserProfile } from '../../../core/models';
import { ValidationService } from '../../../core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';

@Component({
    selector: 'gs-event-updates',
    templateUrl: './event-updates.component.html',
    standalone: false
})
export class EventUpdatesComponent implements OnInit, OnDestroy {
  @Input() eventId: number;
  @Input() eventUserId: number;
  @Input() updates: any;

  form: FormGroup = this.formBuilder.group({
    description: [null, [Validators.required, Validators.maxLength(100000)]],
  });

  updateId: number;
  user: UserProfile;

  styleFormats = [
    {
      title: 'Headings', items: [
        { title: 'Heading 1', format: 'h1' },
        { title: 'Heading 2', format: 'h2' },
        { title: 'Heading 3', format: 'h3' },
        { title: 'Heading 4', format: 'h4' },
        { title: 'Heading 5', format: 'h5' },
        { title: 'Heading 6', format: 'h6' }
      ]
    },
    {
      title: 'Inline', items: [
        { title: 'Bold', format: 'bold' },
        { title: 'Italic', format: 'italic' },
        { title: 'Underline', format: 'underline' },
        { title: 'Strikethrough', format: 'strikethrough' }
      ]
    },
    {
      title: 'Align', items: [
        { title: 'Left', format: 'alignleft' },
        { title: 'Center', format: 'aligncenter' },
        { title: 'Right', format: 'alignright' },
        { title: 'Justify', format: 'alignjustify' }
      ]
    }
  ];

  private editModal: NgbModalRef;
  private subscriptions: Subscription[] = [];

  get isEventOwner(): boolean {
    return this.eventUserId == this.user?.id;
  }

  constructor(
    private eventUpdatesService: EventUpdatesService,
    private toastService: ToastService,
    private ngxUiLoaderService: NgxUiLoaderService,
    private modalService: NgbModal,
    private formBuilder: FormBuilder,
    private store: Store,
    public vs: ValidationService
  ) {

  }

  ngOnInit() {
    this.subscriptions.push(
      this.store.select<UserProfile>(state => state.auth.user)
        .subscribe(user => {
          this.user = user;
        })
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  onAddUpdatesClick(template: TemplateRef<any>) {
    this.clearData();
    this.editModal = this.modalService.open(template, { size: 'lg' });
  }

  onSubmit() {
    if (!this.form.valid) {
      return;
    }

    const model = {
      ...this.form.value,
      id: this.updateId,
      userId: this.user.id,
      eventId: this.eventId,
    };

    if (!this.updateId) {
      this.ngxUiLoaderService.start();
      this.subscriptions.push(
        this.eventUpdatesService.createEventUpdate(model)
          .pipe(
            catchError(e => {
              this.toastService.show('Eroare', e.error?.detail || e.error?.title || 'A aparut o problema neasteptata. Echipa noastra lucreaza pentru a o remedia.', 'error');
              return throwError(e);
            }),
            finalize(() => {
              this.ngxUiLoaderService.stop();
            })
          )
          .subscribe((response: any) => {
            const data = response;
            this.updates.unshift({
              id: data.id,
              eventId: data.eventId,
              userId: data.userId,
              description: data.description,
              createdDate: data.createdDate,
              image: data.image ? environment.contentUrl + '/user-images/' + data.image : null
            });
            this.toastService.show('Succes', 'Noutatea a fost adaugata cu succes.', 'success');
            this.editModal.close();
          })
      );
    } else {
      this.ngxUiLoaderService.start();
      this.subscriptions.push(
        this.eventUpdatesService.updateEventUpdate(this.updateId, model)
          .pipe(
            catchError(e => {
              this.toastService.show('Eroare', e.error?.detail || e.error?.title || 'A aparut o problema neasteptata. Echipa noastra lucreaza pentru a o remedia.', 'error');
              return throwError(e);
            }),
            finalize(() => {
              this.ngxUiLoaderService.stop();
            })
          )
          .subscribe((response: any) => {
            const data = response;
            for (let i = 1; i <= this.updates.length; i++) {
              if (this.updates[i - 1].id == data.id) {
                this.updates[i - 1].description = data.description;
              }
            }
            this.toastService.show('Succes', 'Noutatea a fost modificata cu succes.', 'success');
            this.editModal.close();
          })
      );
    }
  }

  onEditUpdateClick(model, template: TemplateRef<any>) {
    this.updateId = model.id;
    this.eventId = model.eventId;

    this.form.patchValue({
      description: model.description
    });

    this.editModal = this.modalService.open(template);
  }

  onDeleteUpdateClick(id) {
    const modalRef = this.modalService.open(ConfirmComponent);
    modalRef.componentInstance.title = 'Confirma stergerea';
    modalRef.componentInstance.description = `Datele asociate noutatii vor fi permanent sterse si nu pot fi recuperate.`;
    modalRef.componentInstance.acceptLabel = 'Sterge';
    modalRef.componentInstance.acceptClass = 'btn-danger';
    modalRef.componentInstance.rejectLabel = 'Anuleaza';

    this.subscriptions.push(
      modalRef.componentInstance.done
        .subscribe(result => {
          if (result === 'accept') {
            this.ngxUiLoaderService.start();
            this.subscriptions.push(
              this.eventUpdatesService.deleteEventUpdate(id)
                .pipe(
                  catchError(e => {
                    this.toastService.show('Eroare', e.error?.detail || e.error?.title || 'A aparut o problema neasteptata. Echipa noastra lucreaza pentru a o remedia.', 'error');
                    return throwError(e);
                  }),
                  finalize(() => {
                    this.ngxUiLoaderService.stop();
                  })
                )
                .subscribe(() => {
                  this.updates = this.updates.filter(r => r.id !== id);
                  this.toastService.show(
                    'Succes',
                    'Noutatea a fost stearsa.',
                    'success'
                  );
                  // this.editModal.close();
                })
            );
          }
        })
    );
  }

  private clearData() {
    this.form.reset();
    this.updateId = null;
  }
}
